/**
 * LawBrowser – snygg fulltext-läsare för lagar (PBL, Miljöbalken).
 * Visar kapitel-navigation i vänsterspalt + valt kapitel med paragrafer i huvudkolumnen.
 * Sökbart. Anchor-länkar per paragraf. Källa: Riksdagens öppna data.
 */
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, ShieldCheck, ExternalLink, ChevronRight, BookOpen, Menu, X, Link2, Loader2, ArrowLeft, Sparkles, MessageSquare } from "lucide-react";
import Seo from "@/components/Seo";
import ParagraphChat from "@/components/law/ParagraphChat";
import api from "@/lib/api";
import { toast } from "sonner";

const SITE = "https://bygglovkollen.se";

const LAWS = {
  "2010:900": { slug: "pbl", label: "Plan- och bygglagen", short: "PBL" },
  "1998:808": { slug: "miljobalken", label: "Miljöbalken", short: "MB" },
};

// Slug → SFS uppslagning
const SLUG_TO_SFS = Object.entries(LAWS).reduce((a, [k, v]) => ({ ...a, [v.slug]: k }), {});

function highlight(text, query) {
  if (!query || !query.trim()) return text;
  const parts = text.split(new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "gi"));
  return parts.map((p, i) =>
    p.toLowerCase() === query.toLowerCase() ? (
      <mark key={i} className="bg-yellow-200 text-zinc-900 rounded-sm px-0.5">{p}</mark>
    ) : (
      p
    )
  );
}

export default function LawBrowser() {
  const { lawSlug, chapter: urlChapter } = useParams();
  const nav = useNavigate();
  const sfs = SLUG_TO_SFS[lawSlug] || "2010:900";
  const lawMeta = LAWS[sfs];

  const [structure, setStructure] = useState(null);
  const [chapter, setChapter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const activeChapter = urlChapter || "1";

  // Ladda struktur
  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get(`/laws/${sfs}/structure`)
      .then((r) => mounted && setStructure(r.data))
      .catch(() => mounted && toast.error("Kunde inte hämta lagen"))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [sfs]);

  // Ladda valt kapitel
  useEffect(() => {
    if (!activeChapter) return;
    let mounted = true;
    setChapter(null);
    api
      .get(`/laws/${sfs}/chapter/${activeChapter}`)
      .then((r) => mounted && setChapter(r.data))
      .catch(() => mounted && setChapter({ paragraphs: [], _error: true }));
    return () => {
      mounted = false;
    };
  }, [sfs, activeChapter]);

  // Klientssökning inom kapitel
  const filteredParagraphs = useMemo(() => {
    if (!chapter?.paragraphs) return [];
    const q = query.trim().toLowerCase();
    if (!q) return chapter.paragraphs;
    return chapter.paragraphs.filter(
      (p) => p.text.toLowerCase().includes(q) || p.number.toLowerCase().includes(q)
    );
  }, [chapter, query]);

  const selectChapter = (num) => {
    nav(`/lag/${lawSlug || "pbl"}/${num}`);
    setMobileMenuOpen(false);
    setQuery("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const copyLink = (paragraph) => {
    const url = `${SITE}/lag/${lawSlug || "pbl"}/${activeChapter}#${paragraph}`;
    navigator.clipboard?.writeText(url);
    toast.success(`Länk kopierad: ${activeChapter}:${paragraph}`);
  };

  // AI-förklara: cachar per paragraf i minnet
  const [explanations, setExplanations] = useState({});
  const [explainLoading, setExplainLoading] = useState({});
  // Öppna chat-paneler per paragraf-key
  const [openChats, setOpenChats] = useState({});
  const toggleChat = (paragraph) => {
    const key = `${activeChapter}:${paragraph}`;
    setOpenChats((p) => ({ ...p, [key]: !p[key] }));
  };
  const closeChat = (paragraph) => {
    const key = `${activeChapter}:${paragraph}`;
    setOpenChats((p) => ({ ...p, [key]: false }));
  };
  const explain = async (paragraph) => {
    const key = `${activeChapter}:${paragraph}`;
    if (explanations[key]) {
      // Toggle - stäng om öppen
      setExplanations((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
      return;
    }
    setExplainLoading((p) => ({ ...p, [key]: true }));
    try {
      const { data } = await api.post("/laws/explain", {
        sfs,
        chapter: activeChapter,
        paragraph,
      });
      setExplanations((p) => ({ ...p, [key]: data.explanation }));
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) toast.error(typeof detail === "string" ? detail : "För många AI-anrop – vänta lite.");
      else if (status === 402) toast.error("AI-budgeten är slut just nu.");
      else toast.error("Kunde inte generera förklaring");
    } finally {
      setExplainLoading((p) => ({ ...p, [key]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-zinc-50">
      <Seo
        title={`${lawMeta.label} – fulltext`}
        description={`Läs hela ${lawMeta.label} kapitelvis, uppdaterad i realtid från Riksdagens öppna data. Sökbar, mobilanpassad, tydlig typografi.`}
        path={`/lag/${lawSlug}`}
        keywords={`${lawMeta.short}, ${lawMeta.label}, lagtext, paragrafer`}
      />

      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button
              onClick={() => nav("/")}
              data-testid="law-back"
              className="p-2 hover:bg-zinc-100 rounded-sm text-zinc-600"
              aria-label="Tillbaka"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-blue-700">Regelbanken · Fulltext</div>
              <h1 className="text-lg sm:text-xl font-semibold text-zinc-900" style={{ fontFamily: "Outfit" }}>
                {lawMeta.label} ({sfs})
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:inline-flex items-center gap-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2.5 py-1">
              <ShieldCheck className="w-3.5 h-3.5" />
              <span>{structure?.version || "Live från Riksdagen"}</span>
            </div>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="law-menu-toggle"
              className="lg:hidden p-2 hover:bg-zinc-100 rounded-sm"
              aria-label="Öppna kapitelmeny"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
            <input
              data-testid="law-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Sök i ${activeChapter} kap...`}
              className="w-full pl-10 pr-4 py-2.5 border border-zinc-300 rounded-sm text-sm bg-white focus:border-blue-700 focus:ring-2 focus:ring-blue-100 outline-none"
            />
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
        {/* Sidebar */}
        <aside
          className={`${
            mobileMenuOpen ? "block" : "hidden"
          } lg:block fixed lg:sticky lg:top-32 top-0 left-0 z-40 lg:z-auto w-full lg:w-72 flex-shrink-0 h-screen lg:h-[calc(100vh-9rem)] overflow-y-auto bg-white lg:bg-transparent p-4 lg:p-0 border-r lg:border-r-0 border-zinc-200`}
          data-testid="law-sidebar"
        >
          <div className="flex items-center justify-between mb-3 lg:hidden">
            <h2 className="text-sm font-semibold text-zinc-900">Kapitel</h2>
            <button onClick={() => setMobileMenuOpen(false)} className="p-1.5">
              <X className="w-5 h-5" />
            </button>
          </div>
          {loading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 p-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Laddar lagen…
            </div>
          )}
          {structure && (
            <nav className="space-y-0.5">
              {structure.chapters.map((c) => (
                <button
                  key={c.number}
                  onClick={() => selectChapter(c.number)}
                  data-testid={`law-chapter-${c.number}`}
                  className={`w-full text-left px-3 py-2 rounded-sm transition group flex items-start gap-2 ${
                    c.number === activeChapter
                      ? "bg-blue-700 text-white"
                      : "hover:bg-white text-zinc-700 hover:text-blue-700"
                  }`}
                >
                  <span className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 flex-shrink-0 ${c.number === activeChapter ? "text-blue-200" : "text-zinc-400"}`}>
                    {c.number} kap
                  </span>
                  <span className={`text-sm leading-snug ${c.number === activeChapter ? "font-medium" : ""}`}>
                    {c.title}
                  </span>
                </button>
              ))}
            </nav>
          )}
          <div className="mt-4 pt-3 border-t border-zinc-200 text-[11px] text-zinc-500 leading-relaxed">
            <div className="flex items-center gap-1.5 mb-1">
              <ShieldCheck className="w-3 h-3 text-emerald-600" />
              <span className="font-semibold">Källa: Riksdagen</span>
            </div>
            {structure?.source_url && (
              <a
                href={structure.source_url.replace("data.riksdagen.se/dokument/sfs-2010-900.json", "riksdagen.se/sv/dokument-och-lagar/dokument/svensk-forfattningssamling/plan-och-bygglag-2010900_sfs-2010-900/")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-700 hover:underline"
              >
                Öppna originalet <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0" data-testid="law-content">
          {chapter && !chapter._error && (
            <>
              <div className="mb-6">
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-blue-700 mb-2">
                  {chapter.chapter} kap
                </div>
                <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-900" style={{ fontFamily: "Outfit" }}>
                  {chapter.chapter_title}
                </h2>
                <div className="mt-3 text-sm text-zinc-500">
                  {filteredParagraphs.length} av {chapter.paragraphs.length} paragrafer
                  {query && ` matchar "${query}"`}
                </div>
              </div>

              <div className="space-y-4">
                {filteredParagraphs.map((p) => (
                  <article
                    key={p.number}
                    id={p.number}
                    data-testid={`law-para-${p.number}`}
                    className="bg-white border border-zinc-200 rounded-sm p-5 sm:p-6 hover:border-blue-200 transition scroll-mt-32"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 min-w-[52px] text-right">
                        <div className="text-2xl font-semibold text-blue-700 tabular-nums" style={{ fontFamily: "Outfit" }}>
                          {p.number}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500">§</div>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[15px] sm:text-base text-zinc-800 leading-relaxed whitespace-pre-wrap">
                          {highlight(p.text, query)}
                        </p>
                        {explanations[`${chapter.chapter}:${p.number}`] && (
                          <div
                            className="mt-3 border-l-4 border-emerald-500 bg-emerald-50/60 pl-4 pr-3 py-3 rounded-r-sm"
                            data-testid={`law-explain-${p.number}`}
                          >
                            <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-emerald-800 mb-1 inline-flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> AI-förklaring
                            </div>
                            <p className="text-sm text-zinc-800 leading-relaxed">
                              {explanations[`${chapter.chapter}:${p.number}`]}
                            </p>
                          </div>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-3">
                          <button
                            onClick={() => explain(p.number)}
                            data-testid={`law-explain-btn-${p.number}`}
                            disabled={explainLoading[`${chapter.chapter}:${p.number}`]}
                            className="inline-flex items-center gap-1 text-xs text-emerald-700 hover:text-emerald-800 font-medium disabled:opacity-60"
                          >
                            {explainLoading[`${chapter.chapter}:${p.number}`] ? (
                              <><Loader2 className="w-3 h-3 animate-spin" /> Förklarar…</>
                            ) : (
                              <><Sparkles className="w-3 h-3" /> {explanations[`${chapter.chapter}:${p.number}`] ? "Dölj förklaring" : "AI-förklara"}</>
                            )}
                          </button>
                          <button
                            onClick={() => toggleChat(p.number)}
                            data-testid={`law-chat-btn-${p.number}`}
                            className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-800 font-medium"
                          >
                            <MessageSquare className="w-3 h-3" />
                            {openChats[`${chapter.chapter}:${p.number}`] ? "Stäng chatt" : "Chatta med paragrafen"}
                          </button>
                          <button
                            onClick={() => copyLink(p.number)}
                            data-testid={`law-copy-${p.number}`}
                            className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-blue-700 transition"
                          >
                            <Link2 className="w-3 h-3" /> Kopiera länk till {chapter.chapter}:{p.number}
                          </button>
                        </div>
                        {openChats[`${chapter.chapter}:${p.number}`] && (
                          <ParagraphChat
                            sfs={sfs}
                            chapter={chapter.chapter}
                            paragraph={p.number}
                            onClose={() => closeChat(p.number)}
                          />
                        )}
                      </div>
                    </div>
                  </article>
                ))}

                {filteredParagraphs.length === 0 && (
                  <div className="text-center py-16 text-zinc-500">
                    <Search className="w-8 h-8 mx-auto mb-3 opacity-40" />
                    <div>Inga paragrafer matchar &ldquo;{query}&rdquo; i {chapter.chapter} kap.</div>
                    <button onClick={() => setQuery("")} className="mt-3 text-blue-700 hover:underline text-sm">
                      Rensa sökning
                    </button>
                  </div>
                )}
              </div>

              {/* Källa-footer */}
              <div className="mt-10 pt-6 border-t border-zinc-200 text-xs text-zinc-500 flex items-center justify-between flex-wrap gap-2">
                <div className="inline-flex items-center gap-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  Hämtat live från Riksdagens öppna data · {structure?.version}
                </div>
                <a
                  href={structure?.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-700 hover:underline"
                >
                  data.riksdagen.se <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </>
          )}

          {chapter?._error && (
            <div className="bg-white border border-red-200 rounded-sm p-8 text-center">
              <BookOpen className="w-8 h-8 mx-auto text-red-500 mb-3" />
              <h3 className="font-semibold text-zinc-900">Kunde inte ladda kapitel</h3>
              <p className="mt-1 text-sm text-zinc-600">Försök igen eller välj ett annat kapitel i listan.</p>
            </div>
          )}

          {!chapter && !loading && !structure && (
            <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center">
              <BookOpen className="w-8 h-8 mx-auto text-zinc-400 mb-3" />
              <p className="text-sm text-zinc-600">Välj ett kapitel i menyn för att börja läsa.</p>
            </div>
          )}

          {!chapter && !!structure && (
            <div className="flex items-center gap-2 text-sm text-zinc-500 p-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Laddar kapitel {activeChapter}…
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
